import { FormEvent, useState } from 'react';
import { browserSupportsWebAuthn, startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { ArrowLeft, ArrowRight, Check, Copy, KeyRound, LockKeyhole, ShieldCheck, Smartphone, Wifi } from 'lucide-react';
import { Button, Input, Label, TextField } from 'react-aria-components';

import { api, ApiError, type AuthenticatedSession, type LoginChallenge } from '../app/api';

type Phase = 'password' | 'factor' | 'enrollment' | 'totp-enrollment' | 'recovery-codes';

interface LoginScreenProps {
  busy: boolean;
  error: string;
  onLogin: (username: string, password: string) => Promise<LoginChallenge>;
  onAuthenticated: (session: AuthenticatedSession) => Promise<void>;
}

function friendlyError(error: unknown): string {
  if (error instanceof ApiError) {
    const code = error.payload && typeof error.payload === 'object' ? String((error.payload as { error?: unknown }).error || '') : '';
    if (code === 'INVALID_TOTP') return 'Der Authenticator-Code ist nicht gültig.';
    if (code === 'INVALID_RECOVERY_CODE') return 'Der Wiederherstellungscode ist nicht gültig oder wurde bereits verwendet.';
    if (code === 'AUTH_CHALLENGE_EXPIRED') return 'Die Anmeldung ist abgelaufen. Bitte erneut mit Passwort beginnen.';
  }
  if (error instanceof Error && error.name === 'NotAllowedError') return 'Der Passkey-Vorgang wurde abgebrochen oder vom Gerät abgelehnt.';
  return 'Die sichere Anmeldung konnte nicht abgeschlossen werden.';
}

export function LoginScreen({ busy: passwordBusy, error, onLogin, onAuthenticated }: LoginScreenProps) {
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [phase, setPhase] = useState<Phase>('password');
  const [methods, setMethods] = useState<Array<'passkey' | 'totp'>>([]);
  const [localBusy, setLocalBusy] = useState(false);
  const [localError, setLocalError] = useState('');
  const [code, setCode] = useState('');
  const [totpSetup, setTotpSetup] = useState<{ secret: string; uri: string; qrCode: string } | null>(null);
  const [pendingSession, setPendingSession] = useState<AuthenticatedSession | null>(null);
  const webauthnAvailable = browserSupportsWebAuthn();

  const run = async (operation: () => Promise<void>) => {
    setLocalBusy(true);
    setLocalError('');
    try {
      await operation();
    } catch (nextError) {
      setLocalError(friendlyError(nextError));
    } finally {
      setLocalBusy(false);
    }
  };

  const finish = async (session: AuthenticatedSession) => {
    if (session.recoveryCodes?.length) {
      setPendingSession(session);
      setPhase('recovery-codes');
      return;
    }
    await onAuthenticated(session);
  };

  const submitPassword = (event: FormEvent) => {
    event.preventDefault();
    if (passwordBusy || localBusy) return;
    void run(async () => {
      const challenge = await onLogin(username, password);
      setPassword('');
      setMethods(challenge.methods);
      setPhase(challenge.status === 'enrollment_required' ? 'enrollment' : 'factor');
    });
  };

  const authenticatePasskey = () => void run(async () => {
    const optionsJSON = await api.passkeyOptions();
    const response = await startAuthentication({ optionsJSON });
    await finish(await api.verifyPasskey(response));
  });

  const enrollPasskey = () => void run(async () => {
    const optionsJSON = await api.passkeyEnrollmentOptions();
    const response = await startRegistration({ optionsJSON });
    const platform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform;
    await finish(await api.finishPasskeyEnrollment(response, platform || navigator.platform || 'Passkey'));
  });

  const beginTotp = () => void run(async () => {
    setTotpSetup(await api.beginTotpEnrollment());
    setCode('');
    setPhase('totp-enrollment');
  });

  const verifyTotpEnrollment = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => finish(await api.finishTotpEnrollment(code, 'Authenticator-App')));
  };

  const verifyTotp = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => finish(await api.verifyTotp(code)));
  };

  const verifyRecovery = () => void run(async () => finish(await api.verifyRecovery(code)));

  return (
    <main className="login-screen">
      <div className="login-brand"><img src="/icons/nextos-192.png" alt="" /><span>NextOS</span></div>
      <section className="login-panel" aria-live="polite">
        <div className="login-avatar">{phase === 'password' ? <LockKeyhole /> : <ShieldCheck />}</div>
        <h1>NextOS</h1>
        <p>{phase === 'password' ? 'Sicherer Arbeitsdesktop' : phase === 'recovery-codes' ? 'Notfallzugang sichern' : 'Zweiten Faktor bestätigen'}</p>

        {phase === 'password' && <form onSubmit={submitPassword}>
          <TextField value={username} onChange={setUsername} isRequired>
            <Label>Benutzer</Label><Input autoComplete="username" autoFocus />
          </TextField>
          <TextField value={password} onChange={setPassword} isRequired>
            <Label>Passwort</Label><Input type="password" autoComplete="current-password" />
          </TextField>
          {(error || localError) && <div className="login-error" role="alert">{localError || error}</div>}
          <Button type="submit" className="login-submit" isDisabled={passwordBusy || localBusy}>{passwordBusy || localBusy ? 'Prüft …' : <>Weiter <ArrowRight /></>}</Button>
        </form>}

        {phase === 'factor' && <div className="login-methods">
          {methods.includes('passkey') && webauthnAvailable && <Button onPress={authenticatePasskey}><KeyRound /><span><strong>Mit Passkey anmelden</strong><small>Windows Hello, Telefon oder Sicherheitsschlüssel</small></span><ArrowRight /></Button>}
          {methods.includes('totp') && <form onSubmit={verifyTotp} className="totp-form"><TextField value={code} onChange={setCode} isRequired><Label>Authenticator-Code</Label><Input inputMode="numeric" autoComplete="one-time-code" placeholder="000 000" autoFocus /></TextField><Button type="submit" className="login-submit" isDisabled={localBusy}>Bestätigen <ArrowRight /></Button></form>}
          <Button className="recovery-link" onPress={verifyRecovery} isDisabled={!code || localBusy}>Eingegebenen Wert als Wiederherstellungscode verwenden</Button>
          {localError && <div className="login-error" role="alert">{localError}</div>}
        </div>}

        {phase === 'enrollment' && <div className="login-methods">
          <p className="login-instruction">Vor dem ersten Desktop-Zugriff muss mindestens eine sichere zweite Methode eingerichtet werden.</p>
          {webauthnAvailable && <Button onPress={enrollPasskey}><KeyRound /><span><strong>Passkey einrichten</strong><small>Phishing-resistent und am schnellsten</small></span><ArrowRight /></Button>}
          <Button onPress={beginTotp}><Smartphone /><span><strong>Authenticator-App</strong><small>TOTP mit Microsoft, Google, 2FAS oder Aegis</small></span><ArrowRight /></Button>
          {localError && <div className="login-error" role="alert">{localError}</div>}
        </div>}

        {phase === 'totp-enrollment' && totpSetup && <form className="totp-enrollment" onSubmit={verifyTotpEnrollment}>
          <img src={totpSetup.qrCode} alt="QR-Code für die Authenticator-App" />
          <p>QR-Code scannen oder diesen Schlüssel manuell eingeben:</p>
          <code>{totpSetup.secret}</code>
          <TextField value={code} onChange={setCode} isRequired><Label>Ersten Code bestätigen</Label><Input inputMode="numeric" autoComplete="one-time-code" placeholder="000 000" autoFocus /></TextField>
          {localError && <div className="login-error" role="alert">{localError}</div>}
          <Button type="submit" className="login-submit" isDisabled={localBusy}>Aktivieren <Check /></Button>
        </form>}

        {phase === 'recovery-codes' && pendingSession?.recoveryCodes && <div className="recovery-codes">
          <p className="login-instruction">Diese Codes werden nur einmal angezeigt. Jeder Code kann genau einmal verwendet werden.</p>
          <pre>{pendingSession.recoveryCodes.join('\n')}</pre>
          <Button onPress={() => void navigator.clipboard.writeText(pendingSession.recoveryCodes!.join('\n'))}><Copy /> Codes kopieren</Button>
          <Button className="login-submit" onPress={() => void onAuthenticated(pendingSession)}>Ich habe sie sicher gespeichert <ArrowRight /></Button>
        </div>}

        {phase !== 'password' && phase !== 'recovery-codes' && <Button className="login-back" onPress={() => { setPhase('password'); setLocalError(''); setCode(''); }}><ArrowLeft /> Anmeldung neu beginnen</Button>}
        <div className="login-network"><Wifi /> Verschlüsselte WebOS-Verbindung</div>
      </section>
      <time className="login-date">{new Intl.DateTimeFormat('de-CH', { dateStyle: 'full', timeStyle: 'short' }).format(new Date())}</time>
    </main>
  );
}
